import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, BarChart3, PenTool, Camera, Sparkles, ArrowRight, Star, Shield, CheckCircle, Clock, Users, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedCounter } from "@/components/AnimatedCounter";

const HERO_IMAGE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80";

const processSteps = [
  {
    step: "01", icon: Phone, title: "Prise de contact",
    description: "Nous analysons votre bien et vos objectifs lors d'un premier échange personnalisé. Nous discutons de vos attentes, de votre situation et de la meilleure stratégie à adopter.",
    detail: "Visite gratuite de votre bien",
  },
  {
    step: "02", icon: BarChart3, title: "Étude personnalisée",
    description: "Nous réalisons une estimation détaillée de vos revenus potentiels grâce à notre connaissance du marché local. Analyse comparative, taux d'occupation prévisionnel et projection financière.",
    detail: "Simulation de rentabilité offerte",
  },
  {
    step: "03", icon: PenTool, title: "Signature du contrat",
    description: "Nous mettons en place un bail civil en toute transparence. Les conditions sont claires, flexibles et adaptées à votre situation. Aucun engagement sur une durée excessive.",
    detail: "Contrat sans engagement long",
  },
  {
    step: "04", icon: Camera, title: "Mise en location",
    description: "Shooting photo professionnel, création d'annonces optimisées sur Airbnb, Booking et Abritel. Nous mettons tout en place pour maximiser votre visibilité et vos réservations.",
    detail: "En ligne sous 7 jours",
  },
  {
    step: "05", icon: Sparkles, title: "Gestion complète",
    description: "Accueil des voyageurs, ménage inter-séjour, gestion du linge, maintenance, communication... Vous n'avez plus rien à faire. Nous vous envoyons un rapport mensuel détaillé.",
    detail: "Rapport mensuel détaillé",
  },
];

const engagements = [
  { icon: Shield, title: "Transparence totale", desc: "Aucun frais caché. Chaque euro est tracé dans votre rapport mensuel." },
  { icon: Clock, title: "Réactivité <24h", desc: "Notre équipe répond à toute demande ou urgence en moins de 24 heures." },
  { icon: Users, title: "Interlocuteur dédié", desc: "Un seul contact qui connaît votre bien et vos préférences." },
  { icon: Award, title: "Qualité certifiée", desc: "Protocole de nettoyage en 45 points et contrôle qualité systématique." },
];

export default function NotreMethodePage() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div data-testid="methode-page">
      {/* Hero */}
      <section className="relative min-h-[45vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="Notre méthode" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3">
            Processus
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Notre méthode de gestion
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Un processus éprouvé en 5 étapes pour transformer votre bien en source de revenus sans stress.
          </motion.p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-[#1A120B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px lg:-translate-x-px">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#C9922A]/30 to-transparent" />
            </div>
            <div className="relative space-y-16">
              {processSteps.map((item, index) => {
                const Icon = item.icon;
                const isLeft = index % 2 === 0;
                return (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`relative flex items-start gap-6 lg:gap-0 ${isLeft ? "lg:flex-row" : "lg:flex-row-reverse"}`}
                    onMouseEnter={() => setActiveStep(index)}
                    data-testid={`methode-step-${item.step}`}
                  >
                    <div className="absolute left-6 lg:left-1/2 -translate-x-1/2 z-20">
                      <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        activeStep === index ? "border-[#C9922A] bg-[#C9922A]/20 shadow-[0_0_24px_rgba(201,146,42,0.4)]" : "border-[#C9922A]/40 bg-[#1A120B]"
                      }`}>
                        <span className="font-mono text-[#C9922A] text-sm font-bold">{item.step}</span>
                      </div>
                    </div>
                    <div className={`ml-16 lg:ml-0 lg:w-[calc(50%-40px)] ${isLeft ? "lg:pr-0" : "lg:pl-0"}`}>
                      <div className="group p-7 rounded-2xl bg-white/[0.04] backdrop-blur-md border border-white/[0.08] hover:border-[#C9922A]/30 transition-all duration-500 hover:bg-white/[0.07]">
                        <div className="flex items-center gap-3.5 mb-4">
                          <div className="w-10 h-10 rounded-xl bg-[#C9922A]/10 border border-[#C9922A]/20 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#C9922A]" />
                          </div>
                          <h3 className="font-display text-xl text-white group-hover:text-[#E8C874] transition-colors">{item.title}</h3>
                        </div>
                        <p className="text-white/50 leading-relaxed mb-3">{item.description}</p>
                        <span className="inline-flex items-center gap-1.5 text-[#C9922A]/70 text-xs font-medium tracking-wide uppercase">
                          <span className="w-1 h-1 rounded-full bg-[#C9922A]/50" /> {item.detail}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats - Nos résultats */}
      <section className="py-16 bg-[#004333]" data-testid="methode-stats">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-white">Nos résultats parlent d'eux-mêmes</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 98, suffix: "%", label: "Satisfaction", sub: "de nos clients" },
              { value: 85, suffix: "%", label: "Taux d'occupation", sub: "moyen annuel" },
              { value: 7, suffix: " jours", label: "Mise en ligne", sub: "après signature" },
              { value: 30, suffix: "%", prefix: "+", label: "Revenus", sub: "vs location classique" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="text-3xl md:text-4xl font-black text-white mb-1">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} />
                </div>
                <p className="text-[#C9922A] font-semibold text-sm">{stat.label}</p>
                <p className="text-white/40 text-xs">{stat.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos engagements */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="methode-engagements">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Engagements</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Ce que nous vous garantissons</h2>
            <p className="text-[#6B5E50] mt-3 max-w-lg mx-auto">Des promesses concrètes, pas des discours. Voici nos engagements au quotidien.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {engagements.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-5 bg-white rounded-2xl p-6 border border-[#E0D6CA] shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#004333] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#C9922A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004333] mb-1">{item.title}</h3>
                    <p className="text-[#6B5E50] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Témoignage */}
      <section className="py-20 bg-[#EDE8E1]" data-testid="methode-testimonial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#C9922A] text-[#C9922A]" />)}
          </div>
          <blockquote className="text-xl md:text-2xl text-[#004333] italic leading-relaxed mb-8">
            "Le processus a été fluide du début à la fin. En 2 semaines, mon appartement était en ligne avec de superbes photos et les premières réservations sont arrivées rapidement. L'équipe est très professionnelle."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 bg-[#C9922A]/15 rounded-full flex items-center justify-center text-[#C9922A] font-semibold">S</div>
            <div className="text-left">
              <div className="font-semibold text-[#004333]">Sophie R.</div>
              <div className="text-xs text-[#8B7D6B]">Propriétaire à Évaux-les-Bains</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#004333]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">Prêt à démarrer ?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez-nous pour un premier échange gratuit et sans engagement. Nous étudierons ensemble le potentiel de votre bien.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/proprietaires#contact-form" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors" data-testid="methode-cta">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/simulateur" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Simuler mes revenus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
